// Resolves a comms attachment into a usable object URL.
//
// The download route is authenticated with a Bearer token in a header, so
// `<img src>` / `<audio src>` can't point at it directly. We fetch the bytes
// with `authedFetch`, wrap them in an object URL for inline rendering, and
// revoke it on cleanup. Use this for images and voice (which must render
// inline); for plain files prefer `downloadAttachment` (fetch on click, no
// eager prefetch).

import { useEffect, useState } from 'react';
import { authedFetch } from '@apis/auth';
import { attachmentDownloadPath } from '@apis/comms';

interface IAttachmentUrl {
  url: string | null;
  loading: boolean;
  error: boolean;
}

// Session-scoped cache: fileId → object URL.
// Object URLs are never revoked on component unmount — the cache owns them
// for the lifetime of the page. This means switching channels and back never
// re-downloads the same image or voice note.
const urlCache = new Map<string, string>();

// In-flight fetches: fileId → Promise<string>.
// Two components mounting simultaneously with the same fileId share one fetch
// instead of firing duplicate requests.
const inFlight = new Map<string, Promise<string>>();

function fetchAttachmentUrl(siteId: string, fileId: string): Promise<string> {
  const cached = urlCache.get(fileId);
  if (cached) return Promise.resolve(cached);

  const existing = inFlight.get(fileId);
  if (existing) return existing;

  const promise = authedFetch(attachmentDownloadPath(siteId, fileId))
    .then(async (res) => {
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      urlCache.set(fileId, url);
      inFlight.delete(fileId);
      return url;
    })
    .catch((err: unknown) => {
      inFlight.delete(fileId);
      throw err;
    });

  inFlight.set(fileId, promise);
  return promise;
}

export function useAttachmentUrl(siteId: string | undefined, fileId: string): IAttachmentUrl {
  const [state, setState] = useState<IAttachmentUrl>(() => {
    const cached = urlCache.get(fileId);
    if (cached) return { url: cached, loading: false, error: false };
    if (!siteId) return { url: null, loading: false, error: true };
    return { url: null, loading: true, error: false };
  });

  useEffect(() => {
    if (!siteId) return;
    // Already served synchronously from the cache in the initial state — nothing to do.
    if (urlCache.has(fileId)) return;
    let active = true;

    fetchAttachmentUrl(siteId, fileId)
      .then((url) => {
        if (active) setState({ url, loading: false, error: false });
      })
      .catch(() => {
        if (active) setState({ url: null, loading: false, error: true });
      });

    return () => { active = false; };
    // No revokeObjectURL on cleanup — the cache owns the URL for the session.
  }, [siteId, fileId]);

  return state;
}

/**
 * Fetch a file attachment and trigger a browser save. Used for non-inline
 * files so we don't prefetch their bytes just to show a chip.
 */
export async function downloadAttachment(
  siteId: string,
  fileId: string,
  fileName: string,
): Promise<void> {
  const res = await authedFetch(attachmentDownloadPath(siteId, fileId));
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
